import {
  AudioRest,
  IRestAudioCaption
} from '../../common/rest/audio/audioRest';
import { changeCurrentAudioId, setSelectedTargetCaptions } from '../store/current-video/actions';
import { getDispatch, getState } from '../store';
import { addVideoAction, setVideoCaptionsAction, setVideoCaptionsEmpty } from '../store/videos/actions';
import { ICaptionsItem } from '../types/common';
import { AudioPlayerManager } from './audioPlayer/audioPlayerManager';
import { IVideoItem } from '../store/videos/types';
import { getAudioById, getGroupTargetLanguage, getUserGroupById } from '../store/models/selectors';
import { removeAudioFromGroupAction, updateAudioAction } from '../store/models/actions';
import { AuthUserEffects } from './authUserEffects';
import { TAudio } from '../store/models/types';
import { getCurrentAudioId, getSelectedTargetCaption } from '../store/current-video/selectors';
import { getUserStateStorage } from '../../common/utils/local-storage/user-state';

export class AudioEffects {

  // Returns whether the audio loaded — callers should skip dependent side effects (persisting
  // to user-state, starting playback) on false. Never rejects: a failed load clears the captions
  // loader here, and dead-session redirects are handled centrally in requestService.
  public static async load(
    audioId: number,
    groupId: number
  ): Promise<boolean> {
    const dispatch = getDispatch();

    // Enter the loading state immediately, before the fetch, so the previous audio's captions /
    // empty state don't linger on screen while switching audios. The language is known up front
    // from the store audio; on a deep link (no store audio) it's re-synced after the fetch.
    const preState = getState();
    const storeAudio = getAudioById(preState, audioId);
    const preGroup = getUserGroupById(preState, groupId);
    const preLang = storeAudio?.language || (preGroup && getGroupTargetLanguage(preState, preGroup));
    let code = preLang?.code || '';
    dispatch(setVideoCaptionsAction(code, []));
    dispatch(setVideoCaptionsEmpty(false));
    dispatch(setSelectedTargetCaptions({ code, name: preLang?.name!, isAsr: false, url: '' }));

    try {
      const audioRest = await AudioRest.fetch(audioId, groupId);

      const state = getState();
      // The audio may not be in the store on a deep link / F5 (group not loaded, session changed).
      // Fall back to the fetched audio rather than rejecting and hanging the captions loader.
      const audio = storeAudio || audioRest?.audio || audioRest;
      if (!audio) {
        dispatch(setVideoCaptionsEmpty(true));
        return false;
      }

      // Re-resolve the language when the up-front guess was empty (deep link / no store audio).
      if (!code) {
        const group = getUserGroupById(state, groupId);
        const language = audio.language || (group && getGroupTargetLanguage(state, group));
        code = language?.code || '';
        dispatch(setVideoCaptionsAction(code, []));
        dispatch(setSelectedTargetCaptions({ code, name: language?.name || '', isAsr: false, url: '' }));
      }
      if (code) {
        AuthUserEffects.setLang(code);
      }

      const captionsEmpty = audio.captionsCount === 0;

      const storeVideo: IVideoItem = {
        captions: {},
        captionsEmpty,
      };
      dispatch(addVideoAction(storeVideo));

      // Audio loaded — no captions is still a valid, openable audio.
      if (captionsEmpty) return true;

      const captions = AudioEffects.prepareCaptions(await AudioRest.getDefaultCaptions(audioId));
      // No captions came back — show the empty state instead of spinning forever.
      if (!captions.length) {
        dispatch(setVideoCaptionsEmpty(true));
        return true;
      }
      dispatch(setVideoCaptionsAction(code, captions));
      AudioPlayerManager.getInstance().setCaptions(captions)
      return true;
    } catch (e) {
      console.error(e);
      // Dead-session (403/401) redirects are handled centrally in requestService.
      // Never leave the captions loader hanging on any other failed load.
      dispatch(setVideoCaptionsEmpty(true));
      return false;
    }
  }

  private static prepareCaptions(list: IRestAudioCaption[]): ICaptionsItem[] {
    return list.map(c => {
      const text: string = c.captionText || c.words.map(w => w.text).join('');
      return {
        startTime: c.startTime,
        endTime: c.endTime,
        duration: c.endTime - c.startTime,
        words: c.words,
        text,
        userName: c.userName,
        userId: c.userId,
      }
    })
  }

  // for captions compare
  public static setActiveCaptions(list: IRestAudioCaption[]) {
    const dispatch = getDispatch();
    const code = getSelectedTargetCaption(getState())?.code || '';
    const captions = AudioEffects.prepareCaptions(list);
    dispatch(setVideoCaptionsAction(code, captions));
    dispatch(setVideoCaptionsEmpty(captions.length === 0));
    AudioPlayerManager.getInstance().setCaptions(captions);
  }

  public static async remove(audioId: number, groupId: number) {
    const dispatch = getDispatch();
    try {
      await AudioRest.removeAudio(audioId, groupId)
      dispatch(removeAudioFromGroupAction(audioId, groupId))

      const state = getState();
      const currentId = getCurrentAudioId(state);
      if (currentId === audioId) {
        dispatch(changeCurrentAudioId(0));
      }
      // const playerVideoId = getPlayerVideoId(state);
      // if (playerVideoId === videoKey) {
      //   dispatch(setPlayerVideoIdAction(''));
      // }
      const userStorage = getUserStateStorage();
      const data = userStorage.getData();
      if (data.audioId === audioId) {
        userStorage.setAudioInfo(null, data.groupId);
      }

    } catch (e) {
      console.error(e);
    }
  }

  // Manual "Delete audio"/"Delete video": patches the store item in place (the recording itself
  // keeps existing, unlike remove()) using the authoritative fields the backend returns.
  public static async deleteMedia(audioId: number, groupId: number, media: 'audio' | 'video') {
    const dispatch = getDispatch();
    // Same synchronous S3/raw-source deletion cost as a saveLevel change — not instant, so show
    // the tile's loader for the duration.
    dispatch(updateAudioAction(audioId, groupId, { deletingMedia: true }));
    try {
      const audio = await AudioRest.deleteMedia(audioId, media);
      // The backend also downgrades saveLevel (rebuilding what was just deleted is never possible
      // again) — reflect it here so an open settings panel's ladder-disable updates without a reload.
      // roomName rides along too: it's the one field a deletion never clears, so future roomId
      // lookups (RecordingsEffects.getRoomIdForAudio) keep working once audioS3FilePath is gone.
      const patch: Partial<TAudio> = audio
        ? { url: audio.url, audioS3FilePath: audio.audioS3FilePath, videoUrl: audio.videoUrl, saveLevel: audio.saveLevel, roomName: audio.roomName, deletingMedia: false }
        : media === 'audio'
          ? { url: '', audioS3FilePath: '', deletingMedia: false }
          : { videoUrl: null, deletingMedia: false };
      dispatch(updateAudioAction(audioId, groupId, patch));
    } catch (e) {
      console.error(e);
      dispatch(updateAudioAction(audioId, groupId, { deletingMedia: false }));
    }
  }

  public static async update(audioId: number, data: Partial<TAudio>) {
    const dispatch = getDispatch();
    try {
      await AudioRest.updateAudio(audioId, data);
     // dispatch(removeAudioFromGroupAction(audioId, groupId))
    } catch (e) {
      console.error(e);
    }
  }

  private static prepareAudioText(text: string): string {
    return text.replaceAll(' -', '')
      .replaceAll('\n', '\n\n')
  }

}
